import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { safeArray } from '../../utils/safe';
import { FREQUENT_CATEGORY_ID } from '../../utils/posConstants';
import { getFrequencyMap } from '../../utils/frequentProducts';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { ChevronLeft, Combine, DollarSign, HandPlatter, Unlock, Lock, ArrowDownCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { formatCurrency } from '../../utils/format-currency';
import { api } from '../../utils/api';

// Components from central components/POS
import { TableCard } from '../../components/POS/TableCard';
import { ProductPicker } from '../../components/POS/ProductPicker';
import { JoinTablesModal } from '../../components/POS/JoinTablesModal';
import { SplitOrderModal } from '../../components/POS/SplitOrderModal';
import { ServiceRequestsDrawer } from '../../components/POS/ServiceRequestsDrawer';
import { useServiceRequests } from '../../hooks/useServiceRequests';

// Components from other modules
import { TakeawayTile } from '../../components/takeaway/TakeawayTile';
import { PaymentModal } from '../../components/CashRegister/PaymentModal';
import { OpenSessionModal } from '../../components/CashRegister/OpenSessionModal';
import { CloseModal } from '../../components/CashRegister/CloseModal';
import { WithdrawModal } from '../../components/CashRegister/WithdrawModal';
import FlavorSelectorModal from '../../components/Flavors/FlavorSelectorModal';
import AdditionsExceptionsModal from '../../components/Options/OptionsModal';
import { ConfirmCancelOrderModal } from '../../features/orders/components/ConfirmCancelOrderModal';
import { ReceiptRenderer } from '../../components/Receipt/ReceiptRenderer';
import { OrderTicketModal } from '../../components/Receipt/OrderTicketModal';

// Local UI components
import { CartPanel } from './components/CartPanel';
import { TakeawayBoard } from '../../features/pos/TakeawayBoard';
import { Button } from '../../components/ui';

import { PosProvider, usePos } from './PosContext';

interface POSPageProps {
  mode?: 'TABLE' | 'TAKEAWAY';
}

const LiveClock = React.memo(function LiveClock() {
  const [time, setTime] = useState(() => new Date());
  React.useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  return (
    <span className="font-semibold text-slate-600 dark:text-neutral-300 tabular-nums">
      {time.toLocaleTimeString('es-CO')}
    </span>
  );
});

export function POSContent() {
  const navigate = useNavigate();
  const {
    tables, products, categories, selectedTable, setSelectedTable,
    activeCategory, setActiveCategory, searchTerm, setSearchTerm,
    loadingTables, loadingProducts, isTakeawayMode,
    filteredProducts, hasActiveOrder, activeOrder, totals,
    mobileTab, setMobileTab, handleProductClick,
    activeOrderTotal, handleConfirmPayment, processingPayment, pendingProductId,
    refetchActiveOrder,
    showOpenCash, setShowOpenCash, handleOpenSessionConfirm, openingCash,
    cashSession, showCloseCash, setShowCloseCash, closingCash, closeCashSummary,
    handleCloseSessionClick, handleCloseSessionConfirm,
    showWithdrawCash, setShowWithdrawCash, withdrawingCash,
    handleWithdrawCashClick, handleWithdrawCashConfirm,
    receiptToPrint, setReceiptToPrint,
    orderTicketToPrint, setOrderTicketToPrint,
    showPayment, setShowPayment,
    flavorContext, setFlavorContext, optionsKey, setOptionsKey,
    unjoinTableMutation, joinTablesMutation,
    handleCancelActiveOrder, cancelOrderMutation,
    splitOrderMutation, cart, setCart, addProductToCart, addProductToActiveOrder,
  } = usePos();

  const [joinModalOpen, setJoinModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [splitModalOpen, setSplitModalOpen] = useState(false);
  const [openingDrawer, setOpeningDrawer] = useState(false);

  const { requests: serviceRequests, attendRequest } = useServiceRequests();

  const requestsByTableId = useMemo(() => {
    const map = new Map<string, any>();
    for (const req of serviceRequests) {
      map.set(String(req.table_id), req);
    }
    return map;
  }, [serviceRequests]);

  const handleSelectTableFromDrawer = (tableId: number | string) => {
    const found = tables?.find(t => String(t.id) === String(tableId));
    if (found) {
      setSelectedTable(found);
    }
  };

  const handleOpenDrawerOnly = async () => {
    setOpeningDrawer(true);
    try {
      await api.post('/print-jobs/enqueue', {
        type: 'drawer',
      });
      toast.success('Comando de apertura de cajón enviado.');
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Error al enviar comando de apertura.';
      toast.error(msg);
    } finally {
      setOpeningDrawer(false);
    }
  };

  // Categoría virtual "Frecuentes" (solo si ya hay productos usados)
  const categoriesWithFrequent = useMemo(() => {
    const base = categories || [];
    const hasFrequent = Object.keys(getFrequencyMap()).length > 0;
    if (!hasFrequent) return base;
    return [
      { id: FREQUENT_CATEGORY_ID, name: '⭐ Frecuentes', icon: '' },
      ...base,
    ];
  }, [categories, activeCategory]);

  if (!isTakeawayMode && loadingTables) return (
    <div className="flex h-screen items-center justify-center">
      <p className="animate-pulse text-lg font-medium text-muted">Cargando mesas...</p>
    </div>
  );

  if (!isTakeawayMode && !selectedTable) {
    return (
      <div className="min-h-[100dvh] bg-[color:var(--surface-muted)] px-4 pb-24 pt-6 sm:px-6">
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">
          <header className="flex items-center justify-between gap-4">
            <div className="space-y-1">
              <h1 className="text-2xl font-bold tracking-tight">POS - Mesas</h1>
              <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 text-sm text-muted">
                <span>Selecciona una mesa para gestionar pedidos.</span>
                <span className="text-slate-300 dark:text-neutral-700 hidden sm:inline">•</span>
                <LiveClock />
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                onClick={handleOpenDrawerOnly}
                disabled={openingDrawer}
                variant="outline"
                className="gap-2 rounded-full border-amber-500 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/20"
              >
                <Unlock size={18} /> {openingDrawer ? 'Abriendo...' : 'Abrir Cajón'}
              </Button>
              <Button
                onClick={handleWithdrawCashClick}
                variant="outline"
                className="gap-2 rounded-full border-rose-500/80 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/20 shadow-sm"
              >
                <ArrowDownCircle size={18} /> Retiro de Caja
              </Button>
              {cashSession ? (
                <Button
                  onClick={handleCloseSessionClick}
                  disabled={closingCash}
                  className="gap-2 rounded-full bg-rose-600 hover:bg-rose-700 text-white border-none shadow-md shadow-rose-500/20"
                >
                  <Lock size={18} /> Cerrar Caja
                </Button>
              ) : (
                <Button
                  onClick={() => setShowOpenCash(true)}
                  disabled={openingCash}
                  className="gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white border-none shadow-md shadow-emerald-500/20"
                >
                  <Unlock size={18} /> Abrir Caja
                </Button>
              )}
              <Button onClick={() => setJoinModalOpen(true)} variant="outline" className="gap-2 rounded-full">
                <Combine size={18} /> Unir Mesas
              </Button>
            </div>
          </header>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {safeArray(tables).map(t => (
              <TableCard
                key={t.id}
                table={t}
                onSelect={setSelectedTable}
                serviceRequest={requestsByTableId.get(String(t.id))}
                onAttendService={attendRequest}
              />
            ))}
          </div>

          <div className="mt-8 mb-4 flex items-center justify-between">
            <h2 className="text-xl font-bold tracking-tight">Pedidos Para Llevar (Mostrador)</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 pb-8">
            <TakeawayBoard />
          </div>
        </div>
        <JoinTablesModal
          open={joinModalOpen}
          tables={tables || []}
          onClose={() => setJoinModalOpen(false)}
          onConfirm={(p, s) => joinTablesMutation.mutate({ primaryId: p, secondaryId: s })}
          loading={joinTablesMutation.isPending}
        />
        <OpenSessionModal
          visible={showOpenCash}
          onClose={() => setShowOpenCash(false)}
          onConfirm={handleOpenSessionConfirm}
          loading={openingCash}
        />
        <WithdrawModal
          visible={showWithdrawCash}
          onClose={() => setShowWithdrawCash(false)}
          onConfirm={handleWithdrawCashConfirm}
          loading={withdrawingCash}
        />
        <CloseModal
          visible={showCloseCash}
          onClose={() => setShowCloseCash(false)}
          onConfirm={handleCloseSessionConfirm}
          loading={closingCash}
          expectedCash={closeCashSummary?.expectedCash ?? 0}
          paymentBreakdown={closeCashSummary?.paymentBreakdown ?? []}
        />
        <ReceiptRenderer data={receiptToPrint} onComplete={() => setReceiptToPrint(null)} />
        <ServiceRequestsDrawer onSelectTable={handleSelectTableFromDrawer} />
      </div>
    );
  }

  return (
    <div className="flex min-h-[100dvh] flex-col bg-[color:var(--color-bg)] pb-20 md:pb-0">
      {/* Header */}
      <div className="flex items-center gap-4 px-4 pt-4 sm:px-6">
        <button
          onClick={() => isTakeawayMode ? navigate('/pos') : setSelectedTable(null)}
          className="rounded-full p-2 hover:bg-surface-muted transition"
        >
          <ChevronLeft size={24} />
        </button>
        <h1 className="text-xl font-bold flex items-center gap-2">
          {isTakeawayMode ? 'Para Llevar' : `Mesa #${selectedTable?.number}`}
          {!isTakeawayMode && (
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
              hasActiveOrder 
                ? 'bg-amber-105 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300' 
                : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
            }`}>
              <span className={`h-1.5 w-1.5 rounded-full ${hasActiveOrder ? 'bg-amber-500 animate-pulse' : 'bg-emerald-500'}`} />
              {hasActiveOrder ? 'Ocupada' : 'Libre'}
            </span>
          )}
        </h1>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 p-4 md:flex-row md:overflow-hidden">
        {/* Menu Section */}
        <main className={`min-h-0 flex-1 ${mobileTab === 'ORDER' ? 'hidden md:block' : 'block'}`}>
          <ErrorBoundary level="section">
          <ProductPicker
            products={filteredProducts}
            categories={categoriesWithFrequent}
            activeCategory={activeCategory}
            onCategorySelect={setActiveCategory}
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            onProductClick={handleProductClick}
            pendingProductId={pendingProductId}
            isAddingProduct={!!pendingProductId}
            loading={loadingProducts}
          />
          </ErrorBoundary>
        </main>

        {/* Side Panel Section */}
        <aside className={`min-h-0 w-full md:w-[350px] lg:w-[400px] xl:w-[450px] ${mobileTab === 'MENU' ? 'hidden md:block' : 'block'}`}>
          <div className="h-full overflow-y-auto">
            <ErrorBoundary level="section">
              <CartPanel />
            </ErrorBoundary>
          </div>
        </aside>
      </div>

      {/* Modals & Overlays */}
      <PaymentModal
        visible={showPayment}
        total={activeOrderTotal}
        onClose={() => setShowPayment(false)}
        onConfirm={handleConfirmPayment}
        loading={processingPayment}
        orderId={activeOrder?.id}
        order={activeOrder}
        onDiscountChange={refetchActiveOrder}
      />
      <OpenSessionModal
        visible={showOpenCash}
        onClose={() => setShowOpenCash(false)}
        onConfirm={handleOpenSessionConfirm}
        loading={openingCash}
      />
      <WithdrawModal
        visible={showWithdrawCash}
        onClose={() => setShowWithdrawCash(false)}
        onConfirm={handleWithdrawCashConfirm}
        loading={withdrawingCash}
      />

      <CloseModal
        visible={showCloseCash}
        onClose={() => setShowCloseCash(false)}
        onConfirm={handleCloseSessionConfirm}
        loading={closingCash}
        expectedCash={closeCashSummary?.expectedCash ?? 0}
        paymentBreakdown={closeCashSummary?.paymentBreakdown ?? []}
      />
      <ReceiptRenderer data={receiptToPrint} onComplete={() => setReceiptToPrint(null)} />
      <OrderTicketModal data={orderTicketToPrint} onClose={() => setOrderTicketToPrint(null)} />

      {flavorContext && (
        <FlavorSelectorModal
          open={!!flavorContext}
          product={flavorContext.product}
          quantity={1}
          onClose={() => setFlavorContext(null)}
          onConfirm={(payload) => {
            if (hasActiveOrder) {
              addProductToActiveOrder(flavorContext.product, payload);
            } else {
              addProductToCart(flavorContext.product, payload.flavors, payload);
            }
            setFlavorContext(null);
          }}
        />
      )}

      {optionsKey && (
        <AdditionsExceptionsModal
          open={!!optionsKey}
          product={cart[optionsKey]?.product}
          onClose={() => setOptionsKey(null)}
          onApply={(updatedData) => {
            setCart(prev => ({
              ...prev,
              [optionsKey]: { ...prev[optionsKey], ...updatedData }
            }));
            setOptionsKey(null);
          }}
        />
      )}

      {/* Los modales de Dividir/Cancelar ahora se renderizan dentro de CartPanel */}

      {/* Mobile Navigation */}
      <div className="pos-mobile-nav fixed bottom-0 left-0 right-0 z-50 h-16 border-t border-divider bg-surface flex items-center justify-around shadow-lg md:hidden">
        <button
          onClick={() => setMobileTab('MENU')}
          className={`flex-1 flex flex-col items-center justify-center gap-1 transition ${mobileTab === 'MENU' ? 'text-primary' : 'text-muted'}`}
        >
          <HandPlatter size={22} />
          <span className="text-[10px] font-bold uppercase">Menú</span>
        </button>
        <div className="h-8 w-px bg-divider" />
        <button
          onClick={() => setMobileTab('ORDER')}
          className={`flex-1 flex flex-col items-center justify-center gap-1 transition ${mobileTab === 'ORDER' ? 'text-primary' : 'text-muted'}`}
        >
          <DollarSign size={22} />
          <span className="text-[10px] font-bold uppercase">{totals?.total ? formatCurrency(totals.total) : 'Orden'}</span>
        </button>
      </div>

      {/* Drawer de llamadas de mesero / cuentas */}
      <ServiceRequestsDrawer onSelectTable={handleSelectTableFromDrawer} />
    </div>
  );
}

export default function POSPage({ mode = 'TABLE' }: POSPageProps) {
  return (
    <PosProvider mode={mode}>
      <POSContent />
    </PosProvider>
  );
}
